/**
 * Content-aware queries over progress: what is unlocked, what a completion awards.
 */
import { BONUS_FEATURES, SPARKS_BY_ACTIVITY, WEEKLY_CHALLENGE_TARGET, ENVELOPE_DRAWINGS_TO_FILL, type BonusFeatureId } from './constants';
import type { ChallengeDef, ContentPack, CrossoverDef, Drawing, MiniUnit, ObserveDef, PathConfig, PathId, PathUnitItem, PlaygroundItem } from './content/model';
import type { LiveConfig } from './flags';
import { isFeatureUnlocked, type AwardInput, type ProgressState } from './progress';
import { activeChallenge, challengeQualifies } from './social';

export interface ContentIndex {
  pack: ContentPack;
  drawings: Map<string, Drawing>;
  miniUnits: Map<string, MiniUnit>;
  paths: Map<PathId, PathConfig>;
  playground: Map<string, PlaygroundItem>;
  /** mini unit id → its place on a path */
  pathNodes: Map<string, { path: PathConfig; unitIndex: number; item: PathUnitItem; order: number }>;
  /** mini unit id → playground item */
  playgroundByMiniUnit: Map<string, PlaygroundItem>;
  /** v2: level challenges / observation prompts / crossovers by id. */
  levelChallenges: Map<string, ChallengeDef>;
  observe: Map<string, ObserveDef>;
  crossovers: Map<string, CrossoverDef>;
}

export function indexContent(pack: ContentPack): ContentIndex {
  const pathNodes: ContentIndex['pathNodes'] = new Map();
  for (const p of pack.paths) {
    let order = 0;
    p.units.forEach((u, unitIndex) => {
      for (const item of u.items) if (item.miniUnitId) pathNodes.set(item.miniUnitId, { path: p, unitIndex, item, order: order++ });
    });
  }
  return {
    pack,
    drawings: new Map(pack.drawings.map((d) => [d.id, d])),
    miniUnits: new Map(pack.miniUnits.map((m) => [m.id, m])),
    paths: new Map(pack.paths.map((p) => [p.id, p])),
    playground: new Map(pack.playground.items.map((i) => [i.id, i])),
    pathNodes,
    playgroundByMiniUnit: new Map(pack.playground.items.map((i) => [i.miniUnitId, i])),
    levelChallenges: new Map((pack.levelChallenges ?? []).map((c) => [c.id, c])),
    observe: new Map((pack.observe ?? []).map((o) => [o.id, o])),
    crossovers: new Map((pack.crossovers ?? []).map((c) => [c.id, c])),
  };
}

export interface Access {
  /** The active profile has full (premium) access. */
  premium: boolean;
  /** Tester / FTUE-complete override: ignore progress locks. */
  unlockAll: boolean;
  /** Free boundary from live config (default: the content's own `free` flags = 'introFull'). */
  freeBoundary?: LiveConfig['freeBoundary'];
  /** Favourite world (onboarding) for the 'introL2PlusFavourite' boundary. */
  favouritePath?: PathId | null;
  /** Picture tier: world level needed before a picture opens (live config `pictureMinLevel`, default 2). */
  pictureMinLevel?: number;
}

/** Index of the level (unit) holding a path item, -1 if none. */
export const unitIndexOf = (path: PathConfig, item: PathUnitItem) => path.units.findIndex((u) => u.items.some((i) => i.id === item.id));

/**
 * Free boundary (MONETIZATION-PLAN §5.2, remote-configurable): the content marks the generous
 * 'introFull' boundary (whole warm-up + first activities of each world); 'introL2' keeps only warm-up
 * levels 1-2 free; 'introL2PlusFavourite' adds level 1 of the favourite world.
 */
export function isFreeItem(path: PathConfig, item: PathUnitItem, access: Pick<Access, 'freeBoundary' | 'favouritePath'>): boolean {
  // Picture tier is premium, except the content's own free preview (the Intro world's first picture).
  if (item.branch === 'picture') return !!item.free;
  const b = access.freeBoundary ?? 'introFull';
  const ui = unitIndexOf(path, item);
  if (path.id === 'intro') return b === 'introFull' ? true : ui >= 0 && ui <= 1;
  if (item.free) return true;
  return b === 'introL2PlusFavourite' && !!access.favouritePath && access.favouritePath === path.id && ui === 0;
}

/** v2: optional nodes (side branches, crossovers) and nodes grandfathered for this profile never block. */
export const isBlocking = (p: ProgressState, path: PathConfig, item: PathUnitItem) =>
  !!item.miniUnitId && item.gate !== 'optional' && !(p.grandfathered?.[path.id] ?? []).includes(item.miniUnitId);

/** v2: the node is marked "New!" for this profile (inserted into a level it had already reached). */
export const isNewForProfile = (p: ProgressState, path: PathConfig, item: PathUnitItem) =>
  !!item.miniUnitId && (p.grandfathered?.[path.id] ?? []).includes(item.miniUnitId) && !isNodeDone(p, item);

/**
 * A node is done when its mini unit was completed — or (v2 migration §F.3/4) when the same drawing
 * was already completed in the playground (`mu-pg-<drawingId>`): shown done, no sparks again.
 */
export function isNodeDone(p: ProgressState, item: PathUnitItem): boolean {
  if (!item.miniUnitId) return false;
  if (p.completedMiniUnits.includes(item.miniUnitId)) return true;
  return !!item.drawingId && item.activity !== undefined && (item.activity === 'core' || item.activity === 'doodle') && p.completedMiniUnits.includes(`mu-pg-${item.drawingId}`);
}

export type NodeStatus = 'done' | 'current' | 'locked' | 'premium' | 'keyLocked';

/** Lesson nodes in path order. */
export const pathLessons = (p: PathConfig) => p.units.flatMap((u, ui) => u.items.filter((i) => i.miniUnitId).map((i) => ({ item: i, unitIndex: ui })));

export function isPathOpen(p: ProgressState, pathId: PathId, access: Access): boolean {
  if (access.unlockAll) return true;
  if (pathId === 'intro') return p.ftue.stage !== 'playground';
  return p.ftue.stage === 'done';
}

/**
 * Levels in a row (from level 1) whose lessons are all completed. A lesson that the curriculum moved
 * UP to a later level still counts as done there, but it never completes (unlocks) that level on its
 * own: the levels before it must be finished first.
 */
export function completedLevels(p: ProgressState, path: PathConfig): number {
  let n = 0;
  for (const u of path.units) {
    const lessons = u.items.filter((i) => isBlocking(p, path, i));
    if (!lessons.every((i) => isNodeDone(p, i))) break;
    n++;
  }
  return n;
}

/** v2 side branch (challenge / observe): which blocking lessons of its level must be done first. */
function branchOpen(p: ProgressState, path: PathConfig, item: PathUnitItem): boolean {
  const u = path.units[unitIndexOf(path, item)];
  if (!u) return false;
  const cores = u.items.filter((i) => i.activity === 'core' && i.gate !== 'optional');
  const done = cores.filter((i) => isNodeDone(p, i)).length;
  if (item.branch === 'observe') return done >= 1;
  // Challenges: L1 after 2 core lessons, L2-L4 after 3 (CURRICULUM-V2 §C.3).
  return done >= Math.min(cores.length, u.level === 1 ? 2 : 3);
}

/**
 * Status of a lesson node: sequential inside a path (every earlier lesson must be done); the first
 * lesson of level n+1 also needs the key earned by finishing level n. Premium lessons need full access
 * unless marked free. Completed lessons are always 'done', wherever the curriculum put them.
 */
export function nodeStatus(p: ProgressState, path: PathConfig, item: PathUnitItem, access: Access): NodeStatus {
  if (!item.miniUnitId) return 'locked';
  if (isNodeDone(p, item)) return 'done';
  // Tester / demo account (server `unlockAll`): every node open, premium or not (pictures included).
  if (access.unlockAll) return 'current';
  if (!access.premium && !isFreeItem(path, item, access)) return 'premium';
  if (!isPathOpen(p, path.id, access)) return 'locked';
  const unitIndex = unitIndexOf(path, item);
  if (item.branch === 'picture') return pictureNodeOpen(p, path, item, access.pictureMinLevel) ? 'current' : 'locked';
  if (item.branch) {
    if (unitIndex > 0 && (p.pathKeys[path.id] ?? 0) < unitIndex) return 'keyLocked';
    return branchOpen(p, path, item) ? 'current' : 'locked';
  }
  if (!isBlocking(p, path, item)) {
    // Grandfathered / optional main-line node: open once its level is reached.
    if (unitIndex > 0 && (p.pathKeys[path.id] ?? 0) < unitIndex) return 'keyLocked';
    return 'current';
  }
  const lessons = pathLessons(path).filter((l) => isBlocking(p, path, l.item));
  const i = lessons.findIndex((l) => l.item.id === item.id);
  if (i <= 0) return 'current';
  const firstTodo = lessons.findIndex((l) => !isNodeDone(p, l.item));
  if (firstTodo !== i) return 'locked';
  if (unitIndex > 0 && (p.pathKeys[path.id] ?? 0) < unitIndex) return 'keyLocked';
  return 'current';
}

/* --------------------------------- Picture tier --------------------------------- */

/** World level reached (1 + keys earned). */
export const worldLevel = (p: ProgressState, path: PathId) => 1 + (p.pathKeys[path] ?? 0);

/** Picture node: open when its object node is done AND the world level is ≥ `minLevel` (default 2). */
export function pictureNodeOpen(p: ProgressState, path: PathConfig, item: PathUnitItem, minLevel = 2): boolean {
  const obj = path.units.flatMap((u) => u.items).find((i) => i.id === item.pictureOf);
  return !!obj && isNodeDone(p, obj) && worldLevel(p, path.id) >= minLevel;
}

/** Picture of any object (map or playground): the object lesson was done (path node or playground) + world level. */
export function pictureOpen(p: ProgressState, pic: { world: PathId; of: string; activity?: string }, minLevel = 2): boolean {
  const done = [pic.activity ? `mu-${pic.activity}` : '', `mu-pg-${pic.of}`, `mu-${pic.of}`].some((id) => !!id && p.completedMiniUnits.includes(id));
  return done && worldLevel(p, pic.world) >= minLevel;
}

/** Picture nodes of a world (map order). */
export const pathPictures = (path: PathConfig) => path.units.flatMap((u) => u.items.filter((i) => i.branch === 'picture'));

/** A picture done on the map OR from the Playground "Pictures" shelf (`mu-pg-<pictureId>`). */
export const isPictureDone = (p: ProgressState, item: PathUnitItem) => isNodeDone(p, item) || (!!item.drawingId && p.completedMiniUnits.includes(`mu-pg-${item.drawingId}`));

/** Playground-only pictures of a world (their object is not on the map: rainbow cloud, block buddy, balloon bunch…). */
export const playgroundOnlyPictures = (drawings: readonly Drawing[], world: PathId) =>
  drawings.filter((d) => d.activity === 'picture' && d.picture?.world === world && !d.picture.activity).map((d) => d.id);

/**
 * The world's exhibition room: every map picture + the playground-only pictures (`extras`, ids of picture drawings,
 * done when their playground mini unit is completed). Open when all are done (testers: always).
 */
export function exhibitionStatus(p: ProgressState, path: PathConfig, access: Pick<Access, 'unlockAll'>, extras: readonly string[] = []): { open: boolean; done: number; total: number } {
  const pics = pathPictures(path);
  const done = pics.filter((i) => isPictureDone(p, i)).length + extras.filter((id) => p.completedMiniUnits.includes(`mu-pg-${id}`)).length;
  const total = pics.length + extras.length;
  return { open: total > 0 && (done === total || access.unlockAll), done, total };
}

/**
 * Progress migration for curriculum changes (CONTENT_VERSION 2: level moves, new / removed path lessons):
 * completions are kept by mini-unit id; path keys are raised to the levels already completed in a row
 * (never lowered); a current path that no longer exists falls back to the warm-up.
 */
export function migrateProgressToCurriculum(p: ProgressState, paths: PathConfig[]): ProgressState {
  let keys = p.pathKeys;
  for (const path of paths) {
    const k = completedLevels(p, path);
    if (k > (keys[path.id] ?? 0)) keys = { ...keys, [path.id]: k };
  }
  const currentOk = paths.some((x) => x.id === p.currentPath);
  if (keys === p.pathKeys && currentOk) return p;
  return { ...p, pathKeys: keys, ...(currentOk ? {} : { currentPath: 'intro' as PathId }) };
}

/** Share of the world's main-line activities done (side branches don't count; grandfathered "New!" ones do). */
/**
 * Curriculum v2 migration (CONTENT_VERSION 3, docs/CURRICULUM-V2.md §F). Runs once per profile
 * (`curriculumV2At`):
 * 1. frontier = highest level of each world with a key or at least one done activity; every NEW
 *    activity (not on the v1 path) at a level <= frontier is grandfathered: optional "New!" for this profile;
 * 2. completedLevels only counts blocking, non-grandfathered activities; keys are raised, never lowered;
 * 3/4. lessons already drawn in the playground show done on the path (isNodeDone);
 * 5. the shading coin piece moved to `intro-colour-sun`: a profile that finished the v1 3rd warm-up
 *    lesson (`intro-cozy-house`) keeps / gets the shading piece.
 * Turning `curriculumV2` off ignores the extra fields; turning it on again changes nothing (frozen).
 */
export function migrateProgressToCurriculumV2(p: ProgressState, v1Paths: PathConfig[], v2Paths: PathConfig[], now = new Date()): ProgressState {
  if (p.curriculumV2At) return migrateProgressToCurriculum(p, v2Paths);
  const v1Ids = new Set(v1Paths.flatMap((x) => pathLessons(x).map((l) => l.item.miniUnitId!)));
  const grandfathered: Partial<Record<PathId, string[]>> = { ...p.grandfathered };
  for (const path of v2Paths) {
    let frontier = p.pathKeys[path.id] ?? 0;
    path.units.forEach((u, ui) => {
      if (u.items.some((i) => i.miniUnitId && i.gate !== 'optional' && !i.branch && isNodeDone(p, i))) frontier = Math.max(frontier, ui + 1);
    });
    if (p.completedPaths.includes(path.id)) frontier = path.units.length;
    const ids = path.units
      .slice(0, frontier)
      .flatMap((u) => u.items)
      .filter((i) => i.miniUnitId && !i.branch && i.gate !== 'optional' && !v1Ids.has(i.miniUnitId))
      .map((i) => i.miniUnitId!);
    if (ids.length) grandfathered[path.id] = [...new Set([...(grandfathered[path.id] ?? []), ...ids])];
  }
  let coinPieces = p.coinPieces;
  if (p.completedMiniUnits.includes('mu-intro-cozy-house') && !coinPieces.includes('shading')) coinPieces = [...coinPieces, 'shading'];
  const next: ProgressState = { ...p, grandfathered, coinPieces, curriculumV2At: now.toISOString(), updatedAt: now.toISOString() };
  return migrateProgressToCurriculum(next, v2Paths);
}

export function pathFraction(p: ProgressState, path: PathConfig): number {
  const l = pathLessons(path).filter((x) => !x.item.branch && x.item.gate !== 'optional');
  return l.length ? l.filter((x) => isNodeDone(p, x.item)).length / l.length : 0;
}

/** Next lesson to play on a path (first blocking node not completed). */
export function nextPathLesson(p: ProgressState, path: PathConfig): PathUnitItem | undefined {
  return pathLessons(path).find((l) => isBlocking(p, path, l.item) && !isNodeDone(p, l.item))?.item;
}

export function canPlayPlayground(item: PlaygroundItem, access: Access): boolean {
  return access.premium || access.unlockAll || !!item.free;
}

/**
 * v2 crossover ("Crossroads", docs §C.1): hidden silhouette until one of the two worlds is complete
 * (teaser = half revealed), open when both are complete; premium content.
 */
export type CrossoverStatus = 'done' | 'open' | 'teaser' | 'hidden' | 'premium';
export function crossoverStatus(p: ProgressState, c: CrossoverDef, access: Access): CrossoverStatus {
  if (p.completedMiniUnits.includes(`mu-${c.drawingId}`)) return 'done';
  const done = c.worlds.filter((w) => p.completedPaths.includes(w)).length;
  if (done < 2 && !access.unlockAll) return done === 1 ? 'teaser' : 'hidden';
  return access.premium || access.unlockAll ? 'open' : 'premium';
}

/** v2 playground: crossover items stay locked until both worlds are complete. */
export function playgroundItemOpen(p: ProgressState, item: PlaygroundItem, access: Access): boolean {
  if (item.crossover && !access.unlockAll && !item.crossover.every((w) => p.completedPaths.includes(w))) return false;
  return canPlayPlayground(item, access);
}

/** v2 playground levels: level n opens with a level n-1 key in ANY world (docs §D.1.4); done items always open. */
export function playgroundLevelOpen(p: ProgressState, level: number, access: Access): boolean {
  if (access.unlockAll || level <= 1) return true;
  return Math.max(0, ...Object.values(p.pathKeys).map((k) => k ?? 0)) >= level - 1;
}

/* ------------------------------------ Tabs ------------------------------------ */

export type MainTab = 'learn' | 'playground' | 'journal' | 'studio';

export function tabLock(p: ProgressState, tab: MainTab, access: Access, flags: { enableStudio: boolean }): { locked: boolean; reason?: 'drawTwo' | 'sparks' | 'disabled'; sparks?: number } {
  if (tab === 'studio' && !flags.enableStudio) return { locked: true, reason: 'disabled' };
  if (access.unlockAll) return { locked: false };
  if (tab === 'learn') return p.ftue.stage === 'playground' ? { locked: true, reason: 'drawTwo' } : { locked: false };
  if (tab === 'journal' || tab === 'studio') {
    const f: BonusFeatureId = tab;
    if (!isFeatureUnlocked(p, f)) return { locked: true, reason: 'sparks', sparks: BONUS_FEATURES.find((b) => b.id === f)!.sparks - p.totalSparks };
  }
  return { locked: false };
}

/* ---------------------------------- Awards ---------------------------------- */

export interface CompletionContext {
  now: Date;
  /** A drawing was produced (photo captured or capture skipped at the end of a drawing lesson). */
  isDrawing: boolean;
  /** The completion came from the studio or draw-together instead of a path/playground item. */
  kind?: AwardInput['kind'];
  /** v2 level challenge: a photo was taken and the peeks stayed within the allowance → one star. */
  confidentStar?: boolean;
}

/** Build the AwardInput for finishing a mini unit (path key, coin piece, challenge credit...). */
export function completionAward(ix: ContentIndex, p: ProgressState, miniUnitId: string, ctx: CompletionContext): AwardInput {
  const mu = ix.miniUnits.get(miniUnitId);
  const drawing = mu ? ix.drawings.get(mu.drawingId) : undefined;
  const node = ix.pathNodes.get(miniUnitId);
  const pg = ix.playgroundByMiniUnit.get(miniUnitId);
  const firstTime = !p.completedMiniUnits.includes(miniUnitId);
  const input: AwardInput = {
    kind: ctx.kind ?? (node ? 'pathLesson' : 'playground'),
    now: ctx.now,
    miniUnitId,
    isDrawing: ctx.isDrawing,
    skillXp: mu?.skillXp ?? {},
    envelopeSize: ENVELOPE_DRAWINGS_TO_FILL,
  };
  if (pg) input.playgroundItemId = pg.id;
  if (node?.item.activity) input.sparks = SPARKS_BY_ACTIVITY[node.item.activity];
  else if (miniUnitId.startsWith('mu-cross-')) input.sparks = SPARKS_BY_ACTIVITY.crossover;
  if (node?.item.activity === 'challenge' && ctx.confidentStar) input.confidentStar = { path: node.path.id, level: node.path.units[node.unitIndex]?.level ?? node.unitIndex + 1 };
  if (node && firstTime) {
    if (node.item.coinPiece) input.coinPiece = node.item.coinPiece;
    const done = new Set([...p.completedMiniUnits, miniUnitId]);
    // Key for the levels now completed in a row (a lesson moved up a level can't unlock it alone).
    const levels = completedLevels({ ...p, completedMiniUnits: [...done] }, node.path);
    if (levels > completedLevels(p, node.path) && levels > (p.pathKeys[node.path.id] ?? 0)) input.pathKey = { path: node.path.id, level: levels };
    const after = { ...p, completedMiniUnits: [...done] };
    if (pathLessons(node.path).every((l) => !isBlocking(p, node.path, l.item) || isNodeDone(after, l.item))) input.pathCompleted = node.path.id;
  }
  if (drawing && ctx.isDrawing) {
    const active = activeChallenge(ix.pack.challenges, ctx.now);
    input.drawing = { itemId: pg?.id ?? miniUnitId, category: drawing.category, subject: drawing.subject };
    if (active) input.challenge = { id: active.key, qualifies: challengeQualifies(active.challenge, drawing), target: WEEKLY_CHALLENGE_TARGET };
  }
  return input;
}
